function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

/**
 * Detects if the current client is a bot/crawler or a browser-automation tool.
 *
 * Two independent signals: driver flags exposed by the automation runtime, and the
 * user-agent pattern list. Automation that spoofs a stock browser UA is only caught
 * by the former, so neither check subsumes the other.
 *
 * @returns {boolean} True if the client appears to be automated, false otherwise
 */
function isBot() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  if (navigator.webdriver === true) {
    return true;
  }

  if (navigator.languages && navigator.languages.length === 0) {
    return true;
  }

  if (!navigator.userAgent) {
    return false;
  }
  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns =
    /bot|crawl|spider|slurp|teoma|scraper|crawler|headless|phantom|selenium|webdriver|puppeteer|playwright|googlebot|bingbot|yandex|baiduspider|duckduckbot|facebookexternalhit|twitterbot|rogerbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest|slackbot|vkShare|W3C_Validator|whatsapp|flipboard|tumblr|bitlybot|skypeuripreview|nuzzel|qwantify|pinterestbot|bitrix link preview|xing-contenttabreceiver|chrome-lighthouse|telegrambot/i;
  return botPatterns.test(userAgent);
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
//   summary.setAttribute('role', 'button');
//   summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

//   if (summary.nextElementSibling.getAttribute('id')) {
//     summary.setAttribute('aria-controls', summary.nextElementSibling.id);
//   }

//   summary.addEventListener('click', (event) => {
//     if (event.currentTarget.closest('details').classList.contains('mega-menu')) {
//       event.preventDefault();
//       return;
//     }
//     event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
//   });

//   if (summary.closest('header-drawer, menu-drawer')) return;
//   summary.parentElement.addEventListener('keyup', onKeyUpEscape);
// });

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
  document.body.classList.remove('menu-open');
  document.body.classList.remove('overflow-hidden');
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify === 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent(`on${eventName}`, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (const key in params) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    const raw = opt.getAttribute('data-provinces');
    const provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (let i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (let i = 0, count = values.length; i < values.length; i++) {
      const opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    window.addEventListener('resize', this.resizeSetHeaderBottomPosition);
  }

  resizeSetHeaderBottomPosition() {
    const topbar = document.querySelector('.announcement-bar-section');

    document.documentElement.style.setProperty('--header-bottom-position', `${topbar.offsetHeight}px`);
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`,
      );
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);

        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        parentMenuElement.closest('#menu-drawer').classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);

      const offsetTop = this.offsetTopOfViewport();
      const topbar = document.querySelector('.announcement-bar-section');

      if (offsetTop > topbar.getBoundingClientRect().bottom) {
        document.documentElement.style.setProperty('--header-bottom-position', '0px');
      } else {
        /* set new header bottom position */
        document.documentElement.style.setProperty('--header-bottom-position', `${topbar.offsetHeight}px`);
      }
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    /* Add overflow-hidden to html element */
    document.documentElement.classList.add('overflow-hidden');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    document.body.classList.remove('menu-open');
    document.body.classList.remove('overflow-hidden');
    /* Remove overflow-hidden from html element */
    document.documentElement.classList.remove('overflow-hidden');

    elementToFocus?.setAttribute('aria-expanded', false);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    if (
      event.target.parentNode.classList.contains('menu-drawer__close') ||
      event.target.classList.contains('menu-drawer__close') ||
      event.currentTarget.classList.contains('menu-drawer__close')
    ) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      return;
    }
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    if (parentMenuElement.classList.contains('menu-drawer__menu')) {
      parentMenuElement.closest('#menu-drawer').classList.remove('submenu-open');
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`,
      );
    }

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    const topbar = document.querySelector('.announcement-bar-section');
    const offsetTop = this.offsetTopOfViewport();
    if (offsetTop > topbar.getBoundingClientRect().bottom) {
      document.documentElement.style.setProperty('--header-bottom-position', '0px');
    } else {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    }
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    /* Add overflow-hidden to html element */
    document.documentElement.classList.add('overflow-hidden');
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    /* Remove overflow-hidden from html element */
    document.documentElement.classList.remove('overflow-hidden');
  }

  offsetTopOfViewport() {
    return window.scrollY || window.pageYOffset;
  }

  onResize = () => {
    const offsetTop = this.offsetTopOfViewport();

    const topbar = document.querySelector('.announcement-bar-section');
    if (offsetTop > topbar.getBoundingClientRect().bottom) {
      document.documentElement.style.setProperty('--header-bottom-position', '0px');
    } else {
      this.header &&
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(topbar.getBoundingClientRect().bottom)}px`,
        );
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));

    // Auto-load if data-autoload attribute is present
    if (this.hasAttribute('data-autoload')) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.loadContent(false);
      });
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.altCurrentElement = this.querySelector('.alt-slider-count__current');
    this.altTotalElement = this.querySelector('.alt-slider-count__total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.paginationDots = this.querySelectorAll('.slider-counter--item');
    this.progressBar = this.querySelector('.slider-progress-bar');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    // Attach click handlers to existing dots
    if (this.paginationDots.length) {
      this.paginationDots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          const index = parseInt(dot.getAttribute('data-index'), 10);
          if (!isNaN(index)) {
            this.goToSlide(index - 1);
          }
        });
      });
    }
  }

  reinit() {
    // Remove existing event listeners
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.update.bind(this));
    }
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.onButtonClick.bind(this));
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onButtonClick.bind(this));
    }

    // Reinitialize properties
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.altCurrentElement = this.querySelector('.alt-slider-count__current');
    this.altTotalElement = this.querySelector('.alt-slider-count__total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.paginationDots = this.querySelectorAll('.slider-counter--item');
    this.progressBar = this.querySelector('.slider-progress-bar');

    if (!this.slider || !this.nextButton) return;

    // Reinitialize pages and event listeners
    this.initPages();
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    // Reattach click handlers to dots
    if (this.paginationDots.length) {
      this.paginationDots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          const index = parseInt(dot.getAttribute('data-index'), 10);
          if (!isNaN(index)) {
            this.goToSlide(index - 1);
          }
        });
      });
    }

    // Update the slider state and reset to first slide
    this.update();
    this.goToSlide(0);

    // Reset scroll position
    if (this.slider) {
      this.slider.scrollTo({
        left: 0,
        behavior: 'instant',
      });
    }
  }

  onPaginationDotClick(event, dot) {
    event.preventDefault();
    const index = parseInt(dot.dataset.index);
    this.slideScrollPosition = this.sliderItemsToShow[index - 1].offsetLeft;
    this.setSlidePosition(this.slideScrollPosition);
  }

  initPages() {
    // Update to only consider visible items
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0 && !element.classList.contains('hidden'),
    );

    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    // Check if sliderItemOffset is initialized
    if (!this.sliderItemOffset || this.sliderItemOffset === 0) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement && this.querySelector('[data-index]') == null) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    // Update alt slider count
    if (this.altCurrentElement) {
      this.altCurrentElement.textContent = this.currentPage;
    }

    if (this.currentPage != previousPage) {
      if (this.querySelector('[data-index]') != null) {
        document
          .querySelectorAll('[data-index]')
          .forEach((element) => element.classList.remove('slider-counter--current'));
        if (this.querySelector(`[data-index="${this.currentPage}"]`) == null) return;
        this.querySelector(`[data-index="${this.currentPage}"]`).classList.add('slider-counter--current');

        if (this.paginationDots.length) {
          this.paginationDots.forEach((dot) => {
            dot.classList.remove('slider-counter--current');
          });
          this.paginationDots[this.currentPage - 1].classList.add('slider-counter--current');
        }
      }

      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    // Update the progress bar based on the current slide position
    const progressPercentage = (this.currentPage / this.totalPages) * 100;
    if (this.progressBar) {
      this.progressBar.style.width = `${progressPercentage}%`;
    }
    if (this.totalPages == 0 && this.progressBar) {
      this.progressBar.style.width = '0%';
    }

    if (this.enableSliderLooping) return;

    if (!this.sliderItemsToShow || this.sliderItemsToShow.length === 0) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
      if (this.currentPage == 1 && this.progressBar) {
        this.progressBar.style.width = '0%';
      }
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    // Add check for undefined or null elements
    if (!element) return false;

    // Add check for hidden elements
    if (element.classList.contains('hidden')) return false;

    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();

    // Check if sliderItemOffset is initialized
    if (!this.sliderItemOffset || this.sliderItemOffset === 0) return;

    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);

    // Update the progress bar after slide change
    this.update();
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  goToSlide(index) {
    if (!this.slider || !this.sliderItemsToShow || this.sliderItemsToShow.length === 0) return;

    const slideIndex = Math.max(0, Math.min(index, this.sliderItemsToShow.length - 1));
    const slideOffset = this.sliderItemsToShow[slideIndex].offsetLeft;

    this.slider.scrollTo({
      left: slideOffset,
      behavior: 'smooth',
    });
  }

  resetProgressBar() {
    // Reset progress bar to 0
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
    }
    // Force an update to recalculate progress
    this.resetPages();
    this.update();
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');

    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.variantData = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', this.onVariantChange);
    this.invalidVariantIds = [];
  }

  connectedCallback() {
    this.updateOptions();
    this.fetchVariantInventory();
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      console.log('targetxxxx', target);
      this.updateSelectionMetadata(event);

      const variantId = event.target.dataset.variantId;
      const variantData = document.querySelector('[data-selected-variant]');
      const metaData = document.querySelector('[data-selected-variant-metafields]');

      variantData.innerHTML = JSON.stringify({
        variantData,
      });

      metaData.innerHTML = '';

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
          newVariantId: variantId,
        },
      });
    });
  }

  /**
   * Fetch proxy endpoint for variant inventory
   * Ensure that there is inventory in web fulfillment locations
   */
  async fetchVariantInventory() {
    if (this.variantData.length === 0) return;

    const variantIds = this.variantData.map((variant) => `gid://shopify/ProductVariant/${variant.id}`);

    const locale = window.Shopify.country;

    try {
      const response = await fetch('/apps/inventory-levels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          variantIds: variantIds,
          country: locale,
        }),
      });

      const result = await response.json();

      const noInventoryVariants = result?.data?.invalidVariantIds;

      if (noInventoryVariants && noInventoryVariants.length > 0) {
        this.invalidVariantIds = noInventoryVariants?.map((variantId) => variantId?.split('/')?.pop());
        this.disableInvalidVariants();
        this.selectFirstAvailableVariant();
      }
    } catch (error) {
      console.error(`Error Fetching Variant Inventory: ${error}`);
      this.variantInventory = [];
    }
  }

  /**
   * Disable invalid variants
   * If all variants with this option value are out of stock, disable the option
   * If every variant with this option is in the invalid list, disable the option
   */
  disableInvalidVariants() {
    if (!this.invalidVariantIds || this.invalidVariantIds.length === 0) return;

    const fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');

      inputs.forEach((input) => {
        const optionValue = input.value;

        /** Check if all variants with this option value are out of stock **/
        const allVariantsWithThisOption = this.variantData.filter(
          (variant) => variant.options[fieldsetIndex] === optionValue,
        );

        /** Check if every variant with this optin is in the invalid list **/
        const allOutOfStock = allVariantsWithThisOption.every((variant) =>
          this.invalidVariantIds.includes(variant.id.toString()),
        );

        if (allOutOfStock && allVariantsWithThisOption.length > 0) {
          const wrapper = input.closest('.swatch-input__wrapper');

          input.disabled = true;
          input.dataset.inventoryQuantity = '0';
          input.dataset.available = 'false';

          if (wrapper) {
            wrapper.classList.add('sold-out');
            wrapper.dataset.inventoryQuantity = '0';
            wrapper.dataset.available = 'false';
          }
        }
      });
    });
  }

  /**
   * Select the first available variant
   * If no available variants are found, do nothing
   */
  selectFirstAvailableVariant() {
    if (!this.invalidVariantIds || this.invalidVariantIds.length === 0) return;

    const activeInputs = this.querySelectorAll('input[type="radio"]:checked');

    let currentVariantId = null;

    activeInputs.forEach((input) => {
      if (input.dataset.variantId) {
        currentVariantId = input.dataset.variantId;
      }
    });

    if (currentVariantId && this.invalidVariantIds.includes(currentVariantId)) {
      const availableVariant = this.variantData.find(
        (variant) => !this.invalidVariantIds.includes(variant.id.toString()),
      );

      if (availableVariant) {
        this.selectAvailableVariantOptions(availableVariant);
      } else {
        console.warn('No available variants found');
      }
    }
  }

  /**
   * Select the available variant options
   * This function is used to select the available variant options
   */
  selectAvailableVariantOptions(variant) {
    const fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach((fieldset, idx) => {
      const optionValue = variant.options[idx];
      const inputs = fieldset.querySelectorAll('input[type="radio"]');

      inputs.forEach((input) => {
        if (input.value === optionValue) {
          input.click();
        }
      });
    });

    this.updateOptions();
  }

  onVariantChange(event) {
    this.updateOptions();
    this.dispatchEvent(
      new CustomEvent('variant-change', {
        detail: {
          variant: this.currentVariant,
          selectedOptions: this.selectedOptions,
        },
        bubbles: true,
      }),
    );
  }

  updateOptions() {
    // Cache fieldsets to avoid repeated DOM queries
    if (!this._fieldsets) {
      this._fieldsets = Array.from(this.querySelectorAll('fieldset'));
    }

    const fieldsets = this._fieldsets;

    // Use map with early return for better performance
    const selectedOptions = fieldsets.map((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput?.value;
    });

    // Cache color and size fieldset data to avoid repeated DOM queries
    if (!this._colorFieldsetData) {
      this._colorFieldsetData = fieldsets.reduce((acc, fieldset, index) => {
        if (fieldset.querySelector('.swatch-input__wrapper--color')) {
          return { fieldset, index };
        }
        return acc;
      }, null);
    }

    if (!this._sizeFieldsetData) {
      this._sizeFieldsetData = fieldsets.reduce((acc, fieldset, index) => {
        if (fieldset.querySelector('.swatch-input__wrapper--size')) {
          return { fieldset, index };
        }
        return acc;
      }, null);
    }

    // Find current variant more efficiently
    this.currentVariant = this.variantData.find((variant) =>
      variant.options.every((option, index) => selectedOptions[index] === option),
    );

    if (!this.currentVariant) return;

    // If we have both color and size selectors, update all size options when color changes
    if (this._colorFieldsetData && this._sizeFieldsetData) {
      const currentColor = this._colorFieldsetData.fieldset.querySelector('input:checked').value;

      // Create a lookup map for variant availability
      const availabilityMap = new Map();
      this.variantData.forEach((variant) => {
        if (variant.options[this._colorFieldsetData.index] === currentColor) {
          const size = variant.options[this._sizeFieldsetData.index];
          availabilityMap.set(size, availabilityMap.get(size) || variant.available);
        }
      });

      // Update size inputs using the lookup map
      const sizeInputs = this._sizeFieldsetData.fieldset.querySelectorAll('input[type="radio"]');
      sizeInputs.forEach((sizeInput) => {
        const sizeWrapper = sizeInput.closest('.swatch-input__wrapper');
        if (!sizeWrapper) return;

        const hasAvailableVariant = availabilityMap.get(sizeInput.value);
        // sizeWrapper.classList.toggle('linked-product-new--sold-out', !hasAvailableVariant);
        // sizeWrapper.classList.toggle('sold-out', !hasAvailableVariant);
      });
    }

    // Handle active states and single option products
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      inputs.forEach((input) => {
        const wrapper = input.closest('.swatch-input__wrapper');
        if (!wrapper) return;

        // Handle active state
        wrapper.classList.toggle('active', input.checked);
      });
    });

    this.dispatchEvent(
      new CustomEvent('variant-change', {
        detail: {
          variant: this.currentVariant,
          selectedOptions: this.selectedOptions,
        },
        bubbles: true,
      }),
    );
  }

  get selectedOptions() {
    if (this.dataset.isMerch) {
      return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map(
        (input) => input.dataset.optionValueId,
      );
    } else {
      return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((input) => input.value);
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }

  updateDots(slider) {
    // Update pagination dots
    const dotsContainer = slider.querySelector('.slider-counter');
    if (dotsContainer) {
      // Clear existing dots
      dotsContainer.innerHTML = '';

      // Create new dots based on visible slides
      slider.sliderItemsToShow.forEach((_, index) => {
        const dot = document.createElement('span');
        dot.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
       <circle cx="4" cy="4" r="4" fill="black"></circle>
     </svg>`;
        dot.classList.add('slider-counter--item');
        dot.setAttribute('data-index', index + 1);
        dot.setAttribute('aria-label', `Page ${index + 1}`);

        // Set the first dot as current
        if (index === 0) {
          dot.classList.add('slider-counter--current');
        }

        // Add click handler
        dot.addEventListener('click', (event) => {
          slider.onPaginationDotClick(event, dot);
        });

        dotsContainer.appendChild(dot);
      });
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    const sku = target.dataset.variantSku;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;

      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');

      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const parent = target.parentElement;
      const isColorOrSize =
        parent.classList.contains('swatch-input__wrapper--color') ||
        parent.classList.contains('swatch-input__wrapper--size');
      const selectedSwatchValue = target.closest('.product-form__input').querySelector('[data-selected-value]');

      // Get text from the selected radio button's label or value
      let text = target.dataset.optionValue || target.value;

      console.log('target.dataset.optionValue', target.dataset.optionValue);
      console.log('target.value', target.value);

      if (text && isColorOrSize) {
        text = text.split('/').shift();
      }

      target.parentNode.classList.add('active');

      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = text;
      }

      const variantGallery = document.querySelector('.variant-image-gallery');
      if (variantGallery) {
        // Only proceed if we have a current variant
        if (!this.currentVariant) return;

        // Find the color fieldset and get the selected color value
        const colorFieldset = this.querySelector('.swatch-input__wrapper--color')?.closest('fieldset');
        if (!colorFieldset) return;

        const selectedColorInput = colorFieldset.querySelector('input:checked');
        if (!selectedColorInput) return;

        const selectedColor = selectedColorInput.value;

        // Update visibility of media items based on the selected color
        variantGallery.querySelectorAll('.product__media-item').forEach((item) => {
          const itemColor = item.dataset.color;
          const shouldShow = itemColor === selectedColor;

          if (shouldShow) {
            item.classList.remove('hidden');
          } else {
            item.classList.add('hidden');
          }
        });

        // Get the slider component and reinitialize it
        const slider = variantGallery.closest('slider-component');
        if (slider) {
          requestAnimationFrame(() => {
            slider.resetPages();
            slider.update();
            this.updateDots(slider);
          });
        }
      }
    }
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .then(() => {
        window.dispatchEvent(new CustomEvent('recommendationsLoaded'));
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class FeaturedCollectionHandler {
  constructor() {
    this.collectionLinks = document.querySelectorAll('.featured-collection__collection-link');
    this.sectionId = '';
    this.sliderContainer;
    this.cache = new Map(); // Cache for fetched collections
    this.loadingStates = new Map(); // Track loading states to prevent duplicate requests
    this.init();
    this.slider = document.querySelector('.featured-collection slider-component');
  }

  init() {
    this.collectionLinks.forEach((link) => {
      // Click handler
      link.addEventListener('click', () => {
        this.sectionId = link.closest('.featured-collection').dataset.id;
        this.sliderContainer = link
          .closest('.featured-collection__collection-links')
          .parentElement.parentElement.parentElement.querySelector('.slider-wrapper');

        this.collectionSwitch(link);
        this.updateCollectionSection(link);
      });

      // Prefetch on hover for faster switching
      link.addEventListener('mouseenter', () => {
        const collectionHandle = link.dataset.collectionHandle;
        if (collectionHandle && !this.cache.has(collectionHandle) && !this.loadingStates.has(collectionHandle)) {
          this.prefetchCollection(collectionHandle);
        }
      });
    });
    this.updateCollectionSection();
  }

  collectionSwitch(link) {
    const groupedButtons = link
      .closest('.featured-collection__collection-links')
      .querySelectorAll('.featured-collection__collection-link');
    groupedButtons.forEach((btn) => {
      btn.dataset.activeStatus = '';
    });
    link.dataset.activeStatus = 'active';
  }

  async prefetchCollection(collectionHandle) {
    // fetchCollectionHTML handles loading states internally
    const html = await this.fetchCollectionHTML(collectionHandle);
    // Cache is already set in fetchCollectionHTML, but ensure it's there
    if (html && !this.cache.has(collectionHandle)) {
      this.cache.set(collectionHandle, html);
    }
  }

  async fetchCollectionHTML(collectionHandle) {
    // Check cache first
    if (this.cache.has(collectionHandle)) {
      return this.cache.get(collectionHandle);
    }

    // Check if already loading
    if (this.loadingStates.has(collectionHandle)) {
      // Wait for the existing request to complete
      return new Promise((resolve) => {
        const checkCache = () => {
          if (this.cache.has(collectionHandle)) {
            resolve(this.cache.get(collectionHandle));
          } else if (!this.loadingStates.has(collectionHandle)) {
            resolve(null);
          } else {
            setTimeout(checkCache, 50);
          }
        };
        checkCache();
      });
    }

    this.loadingStates.set(collectionHandle, true);

    try {
      // Use the faster sections API approach
      const root = window.Shopify.routes.root === '/' ? '' : window.Shopify.routes.root;
      const separator = root.endsWith('/') ? '' : '/';
      const url = `${root}${separator}collections/${collectionHandle}?view=fetch`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();

      // Parse and extract the slider-wrapper content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const sliderWrapper = doc.querySelector('.slider-wrapper');

      if (sliderWrapper) {
        const content = sliderWrapper.innerHTML;
        this.cache.set(collectionHandle, content);
        return content;
      }

      return null;
    } catch (error) {
      console.error('Error fetching collection:', error);
      return null;
    } finally {
      this.loadingStates.delete(collectionHandle);
    }
  }

  updateCollectionSection(link) {
    if (!link) return;

    // Update view all link immediately
    const viewAllLinks = link
      .closest('.featured-collection__collection-links')
      .parentElement.parentElement.parentElement.querySelectorAll('.view-all-link');

    viewAllLinks.forEach((viewAllLink) => {
      const root = window.Shopify.routes.root === '/' ? '' : window.Shopify.routes.root;
      const separator = root.endsWith('/') ? '' : '/';
      viewAllLink.href = `${root}${separator}collections/${link.dataset.collectionHandle}`;
    });

    const groupedButtons = link
      .closest('.featured-collection__collection-links')
      .querySelectorAll('.featured-collection__collection-link');

    groupedButtons.forEach((linkItem) => {
      if (linkItem.dataset.activeStatus == 'active') {
        const collectionHandle = linkItem.dataset.collectionHandle;

        // Show loading state immediately
        this.showLoadingState();

        // Fetch and update
        this.fetchCollectionHTML(collectionHandle)
          .then((html) => {
            if (html && this.sliderContainer) {
              // Use requestAnimationFrame for smoother DOM updates
              requestAnimationFrame(() => {
                this.sliderContainer.innerHTML = html;

                // Find the slider-component that contains this sliderContainer
                const sliderComponent = this.sliderContainer.closest('slider-component');

                // Reinitialize the slider after DOM update
                if (sliderComponent && typeof sliderComponent.reinit === 'function') {
                  requestAnimationFrame(() => {
                    sliderComponent.reinit();
                    // Hide loading state after slider is reinitialized
                    this.hideLoadingState();
                  });
                } else {
                  // Hide loading state even if slider reinit fails
                  this.hideLoadingState();
                }
              });
            } else {
              // Hide loading state if no HTML was returned
              this.hideLoadingState();
            }
          })
          .catch((error) => {
            console.error('Error updating collection section:', error);
            // Hide loading state on error
            this.hideLoadingState();
          });
      }
    });
  }

  showLoadingState() {
    if (!this.sliderContainer) return;

    // Remove any existing loader first
    const existingLoader = this.sliderContainer.querySelector('.collection-loading');
    if (existingLoader) {
      existingLoader.remove();
    }

    // Ensure container has relative positioning
    if (getComputedStyle(this.sliderContainer).position === 'static') {
      this.sliderContainer.style.position = 'relative';
    }

    // Create loading overlay
    const loader = document.createElement('div');
    loader.className = 'collection-loading';
    loader.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      pointer-events: none;
      transition: opacity 0.3s ease;
    `;

    // Create spinner
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 40px;
      height: 40px;
      border: 3px solid rgba(0, 0, 0, 0.1);
      border-top-color: rgba(0, 0, 0, 0.6);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    `;

    // Add keyframes animation if not already in document
    if (!document.getElementById('collection-loading-styles')) {
      const style = document.createElement('style');
      style.id = 'collection-loading-styles';
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    loader.appendChild(spinner);
    this.sliderContainer.appendChild(loader);

    // Store reference for removal
    this.currentLoader = loader;
  }

  hideLoadingState() {
    if (this.currentLoader && this.currentLoader.parentNode) {
      this.currentLoader.style.opacity = '0';
      setTimeout(() => {
        if (this.currentLoader && this.currentLoader.parentNode) {
          this.currentLoader.remove();
        }
        this.currentLoader = null;
      }, 300);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FeaturedCollectionHandler();
});

class FeaturedBlogHandler {
  constructor() {
    this.blogLinks = document.querySelectorAll('.blog__blog-link');
    this.sectionId = '';
    this.sliderContainer;
    this.init();
  }

  init() {
    this.blogLinks.forEach((link) => {
      link.addEventListener('click', () => {
        this.sectionId = link.closest('.featured-blog').dataset.id;
        this.sliderContainer = link.closest('.blog__blog-links').nextElementSibling;
        this.blogSwitch(link);
        this.updateBlogSection(link, this.sliderContainer);
      });
    });
    this.updateBlogSection();
  }

  blogSwitch(link) {
    const groupedButtons = link.closest('.blog__blog-links').querySelectorAll('.blog__blog-link');
    groupedButtons.forEach((btn) => {
      btn.dataset.activeStatus = '';
    });
    link.dataset.activeStatus = 'active';
  }

  updateBlogSection(link, container) {
    // Get active collection
    if (link) {
      const currentActiveFilter = link.getAttribute('data-tag-handle');
      const blogWrapper = link.closest('.featured-blog');
      const blogsContainer = blogWrapper.querySelector('[js-blog-wrapper="blogs"]');

      if (currentActiveFilter === 'show-all') {
        blogsContainer.querySelectorAll('[js-blog="blog"]').forEach((blog) => {
          blog.classList.remove('hidden');
        });

        blogsContainer.scrollLeft = 0;
        return;
      }

      blogsContainer.querySelectorAll('[js-blog="blog"]').forEach((blog) => {
        const roleValue = blog.getAttribute('js-blog-tags');

        if (!roleValue.includes(currentActiveFilter)) {
          blog.classList.add('hidden');
        } else {
          blog.classList.remove('hidden');
        }
      });

      blogsContainer.scrollLeft = 0;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FeaturedBlogHandler();
});
